import style from "../../booking/style.module.scss";
import { useEffect, useRef, useState } from "react";
import { Image } from "antd";

interface GateInDetailsProps {
  value: any;
}

export const GateInDetails = ({ value }: GateInDetailsProps) => {
  const {
    reference = "-",
    gateOutReference = "-",
    createdAt = "-",

    restitutionSource: {
      id: restitutionSourceId = "-",
      name: restitutionSourceName = "-",
    } = {},

    checkedBy: {
      fullname = "-",
      email = "-",
    } = {},

    bookingReference = "-",
    bookingBlNumber = "-",

    destination: {
      name: destination = "-",
    } = {},

    previousStorage: {
      name: previousStorage = "-",
    } = {},

    containerReference = "-",
    sealNumber = "-",
    additionalSealNumbers = [],

    containerFillingStatus: {
      name: containerFillingStatus = "-",
    } = {},

    containerTonnage = "-",

    vehicleType: {
      id: vehicleTypeId = "-",
      name: vehicleTypeName = "-",
    } = {},

    truck: {
      reference: truckReference = "-",

      materialFrom: {
        name: truckMaterialProvenance = "-",
      } = {},

      driver: {
        fullname: driverFullname = "-",
        email: driverEmail = "-",
      } = {},
    } = {},

    driverLicenseNumber = "-",
    driverIdCardNumber = "-",

    customerRemovalControl: {
      containerRestitutionDelay: {
        label: delay = "-",
      } = {},
    } = {},

    damages = [],
    comment = "-",
  } = value || {};

  const images = value?.images ?? [];

  const imagesContainerRef = useRef<HTMLDivElement>(null);

  const [visibleCount, setVisibleCount] = useState(images.length);
  const [showAllImages, setShowAllImages] = useState(false);

  useEffect(() => {
    const container = imagesContainerRef.current;

    if (!container || !images.length) {
      return;
    }

    const calculateVisibleImages = () => {
      const containerWidth = container.clientWidth;

      const imageWidth = 140;
      const gap = 12;

      const count = Math.floor(
        (containerWidth + gap) / (imageWidth + gap)
      );

      setVisibleCount(Math.max(1, count));
    };

    calculateVisibleImages();

    const resizeObserver = new ResizeObserver(
      calculateVisibleImages
    );

    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [images.length]);

  return (
    <div className={style.details}>
      {/* Informations sur la restitution */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          Informations sur la restitution
        </h3>

        <div className={style.details__grid}>
          <div className={style.details__item}>
            <span>Référence Restitution (Gate In)</span>
            <strong>{reference}</strong>
          </div>

          <div className={style.details__item}>
            <span>Référence Gate Out associée</span>
            <strong>{gateOutReference}</strong>
          </div>

          <div className={style.details__item}>
            <span>Source de la restitution</span>
            <strong>{restitutionSourceName}</strong>
          </div>

          <div className={style.details__item}>
            <span>Date de restitution</span>
            <strong>{createdAt}</strong>
          </div>

          <div className={style.details__item}>
            <span>Contrôlé par</span>
            <strong>{fullname}</strong>

            {email !== "-" && (
              <small
                className={style.details__item__secondary}
              >
                {email}
              </small>
            )}
          </div>

          <div className={style.details__item}>
            <span>Bilan du délai de retour</span>
            <strong>{delay}</strong>
          </div>
        </div>
      </section>

      {/* Informations Booking */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          Informations Booking
        </h3>

        <div className={style.details__grid}>
          <div className={style.details__item}>
            <span>N° Booking</span>
            <strong>{bookingReference}</strong>
          </div>

          <div className={style.details__item}>
            <span>N° BL</span>
            <strong>{bookingBlNumber}</strong>
          </div>
        </div>
      </section>

      {/* Informations du conteneur */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          {restitutionSourceId === "DIRECT" ? "Informations du conteneur" : "Informations du conteneur avant restitution"}
        </h3>

        <div className={style.details__grid}>
          <div className={style.details__item}>
            <span>Identifiant conteneur</span>
            <strong>{containerReference}</strong>
          </div>

          <div className={style.details__item}>
            <span>Statut de remplissage</span>
            <strong>{containerFillingStatus}</strong>
          </div>

          <div className={style.details__item}>
            <span>Tonnage</span>
            <strong>
              {containerTonnage !== "-"
                ? `${containerTonnage} T`
                : "-"}
            </strong>
          </div>

          <div className={style.details__item}>
            <span>N° de scellé</span>
            <strong>{sealNumber}</strong>
          </div>

          <div className={style.details__item}>
            <span>Scellés supplémentaires</span>

            <strong>
              {additionalSealNumbers.length > 0
                ? additionalSealNumbers.join(", ")
                : "-"}
            </strong>
          </div>
        </div>
      </section>

      {/* Emplacement */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          Emplacement
        </h3>

        <div className={style.details__grid}>
          <div className={style.details__item}>
            <span>Parc précédent</span>
            <strong>{previousStorage}</strong>
          </div>

          <div className={style.details__item}>
            <span>Parc de stockage de destination</span>
            <strong>{destination}</strong>
          </div>
        </div>
      </section>

      {/* Transport */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          Transport
        </h3>

        <div className={style.details__grid}>
          <div className={style.details__item}>
            <span>Type de véhicule</span>
            <strong>{vehicleTypeName}</strong>
          </div>

          {vehicleTypeId === "INTERNAL" && (
            <>
              <div className={style.details__item}>
                <span>Camion</span>
                <strong>{truckReference}</strong>
              </div>

              <div className={style.details__item}>
                <span>Provenance</span>
                <strong>{truckMaterialProvenance}</strong>
              </div>

              <div className={style.details__item}>
                <span>Conducteur</span>
                <strong>{driverFullname}</strong>

                {driverEmail !== "-" && (
                  <small
                    className={
                      style.details__item__secondary
                    }
                  >
                    {driverEmail}
                  </small>
                )}
              </div>
            </>
          )}

          <div className={style.details__item}>
            <span>N° permis de conduire</span>
            <strong>{driverLicenseNumber}</strong>
          </div>

          <div className={style.details__item}>
            <span>N° pièce d'identité</span>
            <strong>{driverIdCardNumber}</strong>
          </div>
        </div>
      </section>

      {/* Avaries */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          Avaries constatées
        </h3>

        {damages.length > 0 ? (
          <div className={style.details__damages}>
            {damages.map(
              (damage: any, index: number) => (
                <div
                  key={damage.id ?? index}
                  className={style.details__damage}
                >
                  {damage.name ?? "-"}
                </div>
              )
            )}
          </div>
        ) : (
          <span className={style.details__empty}>
            Aucune avarie constatée.
          </span>
        )}
      </section>

      {/* Commentaire */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          Commentaire
        </h3>

        <div className={style.details__comment}>
          {comment !== "-"
            ? comment
            : "Aucun commentaire."}
        </div>
      </section>

      {/* Images */}
      <section className={style.details__section}>
        <h3 className={style.details__title}>
          Images
        </h3>

        {images.length > 0 ? (
          <div
            ref={imagesContainerRef}
            className={`${style.details__images} ${
              showAllImages
                ? style.details__images__expanded
                : ""
            }`}
          >
            {images.map(
              (image: any, index: number) => {
                const hasMoreImages =
                  images.length > visibleCount;

                const isLastVisibleImage =
                  !showAllImages &&
                  hasMoreImages &&
                  index === visibleCount - 1;

                return (
                  <div
                    key={image["@id"] || index}
                    className={style.details__image}
                  >
                    <Image
                      src={image.contentUrl}
                      alt={
                        image.reference ||
                        `Image ${index + 1}`
                      }
                      preview={!isLastVisibleImage}
                      className={
                        isLastVisibleImage
                          ? style.details__image__blurred
                          : ""
                      }
                    />

                    {isLastVisibleImage && (
                      <div
                        className={
                          style.details__image__overlay
                        }
                        onClick={() =>
                          setShowAllImages(true)
                        }
                      >
                        <span>
                          Voir toutes les images
                        </span>
                      </div>
                    )}
                  </div>
                );
              }
            )}

            {showAllImages &&
              images.length > visibleCount && (
                <button
                  type="button"
                  className={
                    style.details__imagesToggle
                  }
                  onClick={() =>
                    setShowAllImages(false)
                  }
                >
                  Réduire les images
                </button>
              )}
          </div>
        ) : (
          <div className={style.details__empty}>
            Aucune image.
          </div>
        )}
      </section>
    </div>
  );
};